import React, { useState } from 'react';
import { Form, Row, Col, Button } from 'react-bootstrap';
import { utils, writeFile } from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { TbLoader3 } from 'react-icons/tb'; // Importing the loader icon
import './pf-user.css';

// Dummy data for demonstration
const mockData = Array.from({ length: 50 }, (_, index) => ({
  SR: index + 1,
  Name: `Employee ${index + 1}`,
  Role: `Role ${index + 1}`,
  Description: `Description ${index + 1}`,
  Date: `2023-08-${(index % 31) + 1}`,
}));

export default function PFUser() {
  const rowsPerPage = 10;
  const [currentPage, setCurrentPage] = useState(1);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [showTable, setShowTable] = useState(false);
  const [data, setData] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false); // For loading state

  const validateForm = () => {
    let errors = {};
    let formIsValid = true;

    if (!startDate) {
      formIsValid = false;
      errors.startDate = 'Start date is required';
    }

    if (!endDate) {
      formIsValid = false;
      errors.endDate = 'End date is required';
    }

    if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
      formIsValid = false;
      errors.endDate = 'End date should be greater than or equal to start date';
    }

    setFormErrors(errors);
    return formIsValid;
  };

  const handleGenerateReport = () => {
    if (validateForm()) {
      setIsGenerating(true); // Set loading state
      setTimeout(() => { // Simulate async operation
        // Simulate data fetching based on pagination
        const startIndex = (currentPage - 1) * rowsPerPage;
        const endIndex = startIndex + rowsPerPage;
        setData(mockData.slice(startIndex, endIndex));
        setShowTable(true);
        setIsGenerating(false); // Clear loading state
      }, 2000); // Simulated delay
    } else {
      setShowTable(false); // If the form is invalid, hide the table
    }
  };

  const handleExportToExcel = () => {
    const worksheet = utils.json_to_sheet(data);
    const workbook = utils.book_new();
    utils.book_append_sheet(workbook, worksheet, 'Provident Fund Report');
    writeFile(workbook, 'Provident_Fund_Report.xlsx');
  };

  const handleExportToPDF = () => {
    const doc = new jsPDF();
    doc.text('Provident Fund Report', 14, 16);
    doc.autoTable({
      startY: 20,
      head: [['SR', 'Name', 'Role', 'Description', 'Date']],
      body: data.map(item => [item.SR, item.Name, item.Role, item.Description, item.Date]),
    });
    doc.save('Provident_Fund_Report.pdf');
  };

  // Handle page change
  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= Math.ceil(mockData.length / rowsPerPage)) {
      setCurrentPage(pageNumber);
      // Fetch data for the new page
      const startIndex = (pageNumber - 1) * rowsPerPage;
      const endIndex = startIndex + rowsPerPage;
      setData(mockData.slice(startIndex, endIndex));
    }
  };

  // Calculate page range
  const pageRange = () => {
    const pages = [];
    const totalPages = Math.ceil(mockData.length / rowsPerPage);
    let startPage = Math.max(currentPage - 1, 1);
    let endPage = Math.min(startPage + 2, totalPages);

    if (endPage - startPage < 2) {
      startPage = Math.max(endPage - 2, 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };

  return (
    <div className='container-fluid'>
      <h1 className='pf-user-title'>Employee's Provident Fund Report</h1>
      <hr />
      <h1 className='pf-user-subtitle'>Employee's Name</h1>
      <hr />
      <Form className="pf-user-form">
        <Row>
          <Col md={6}>
            <Form.Group>
              <Form.Label htmlFor="start_date">
                Start date <span className="required">*</span> :
              </Form.Label>
              <Form.Control
                type="date"
                id="start_date"
                name="start_date"
                required
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                isInvalid={!!formErrors.startDate}
              />
              <Form.Control.Feedback type="invalid">
                {formErrors.startDate}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label htmlFor="end_date">
                End date <span className="required">*</span> :
              </Form.Label>
              <Form.Control
                type="date"
                id="end_date"
                name="end_date"
                required
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                isInvalid={!!formErrors.endDate}
              />
              <Form.Control.Feedback type="invalid">
                {formErrors.endDate}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Row>
        <Button
          className="generate-report-button"
          type="button"
          onClick={handleGenerateReport}
          disabled={isGenerating}
        >
          {showTable ? 'Generate Report' : (isGenerating ? (
            <>
              <TbLoader3 className="icon-rotate" />
              Generating...
            </>
          ) : (
            'Generate Report'
          ))}
        </Button>
      </Form>

      {showTable && (
        <div className="pf-user-table-container">
          <div className="table-actions">
            <Row className="w-100">
              <Col xs={12} md={6} className="d-flex align-items-center mb-3 justify-content-start">
                <Button variant="secondary" className="export-button" onClick={handleExportToExcel}>Excel</Button>
                <Button variant="secondary" className="export-button" onClick={handleExportToPDF}>PDF</Button>
              </Col>
              <Col xs={12} md={6} className="d-flex align-items-center mb-3 justify-content-end">
                <Form className="d-flex w-100 justify-content-end">
                  <Form.Control
                    type="search"
                    placeholder="Search"
                    aria-label="Search"
                    className="search-bar"
                  />
                </Form>
              </Col>
            </Row>
          </div>
          <div className="table-responsive">
            <table className="table table-striped">
              <thead className="pf-user-table-header">
                <tr className='pf-header'>
                  <th>SR</th>
                  <th>Name</th>
                  <th>Role</th>
                  <th>Description</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {data.length > 0 ? (
                  data.map(item => (
                    <tr key={item.SR}>
                      <td>{item.SR}</td>
                      <td>{item.Name}</td>
                      <td>{item.Role}</td>
                      <td>{item.Description}</td>
                      <td>{item.Date}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="text-center">No data Available Now</td>
                  </tr>
                )}
              </tbody>
              <tfoot className='pf-footer'>
                <tr className="pf-user-table-footer">
                  <td>Total</td>
                  <td>{data.length}</td>
                  <td>{data.length}</td>
                  <td>{data.length}</td>
                  <td>{data.length}</td>
                </tr>
              </tfoot>
            </table>

            <div className="pagination-container">
              <Row className="custom-row">
                <Col xs={12} md={9} className="custom-col-left">
                  Showing {(currentPage - 1) * rowsPerPage + 1} to {Math.min(currentPage * rowsPerPage, mockData.length)} of {mockData.length} Entries
                </Col>
                <Col xs={12} md={3} className="custom-col-right">
                  <nav>
                    <ul className="pagination">
                      <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <a
                          href="/#"
                          className="page-link"
                          onClick={() => handlePageChange(currentPage - 1)}
                        >
                          Previous
                        </a>
                      </li>
                      {pageRange().map((page) => (
                        <li key={page} className={`page-item ${currentPage === page ? 'active' : ''}`}>
                          <a
                            href="/#"
                            className="page-link"
                            onClick={() => handlePageChange(page)}
                          >
                            {page}
                          </a>
                        </li>
                      ))}
                      <li className={`page-item ${currentPage === Math.ceil(mockData.length / rowsPerPage) ? 'disabled' : ''}`}>
                        <a
                          href="/#"
                          className="page-link"
                          onClick={() => handlePageChange(currentPage + 1)}
                        >
                          Next
                        </a>
                      </li>
                    </ul>
                  </nav>
                </Col>
              </Row>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
